import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RejectedFile } from '../../../models/rejected-file';

export interface ImportHint {
  label: string;
  highlight?: boolean;
}

@Component({
  selector: 'app-document-import-zone',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule],
  templateUrl: './document-import-zone.component.html',
  styleUrls: ['./document-import-zone.component.scss'],
})
export class DocumentImportZoneComponent {
  @Input() variant: 'dropzone' | 'compact' = 'dropzone';
  @Input() accept = '.pdf,.docx,.xlsx,.doc,.xls,.rtf,.txt,.odt,.zip,.rar';
  @Input() multiple = true;
  @Input() disabled = false;
  @Input() mainTitle = '';
  @Input() subtitle = '';
  @Input() addLabel = 'tenders.responses.documents.add-more';
  @Input() browseLabel = 'tenders.responses.documents.browse';
  @Input() showHints = true;
  @Input() hints: ImportHint[] | null = null;
  @Input() footnote = '';

  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() filesRejected = new EventEmitter<RejectedFile[]>();

  @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>;

  isDragging = false;
  // Drag depth counter: dragleave fires when crossing child elements, so a plain
  // boolean flickers; only clear once every enter is balanced by a leave.
  private dragCounter = 0;

  triggerFileInput(): void {
    if (this.disabled) return;
    this.fileInputRef?.nativeElement.click();
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) this.emitFiles(Array.from(input.files));
    input.value = '';
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    if (this.disabled) return;
    this.dragCounter++;
    this.isDragging = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      this.isDragging = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter = 0;
    this.isDragging = false;
    if (this.disabled) return;

    const items = event.dataTransfer?.items;
    if (!items) return;

    const files: File[] = [];
    const rejected: RejectedFile[] = [];

    for (const item of Array.from(items)) {
      const entry = (
        item as unknown as { webkitGetAsEntry?: () => { isDirectory: boolean; isFile: boolean; name: string } }
      ).webkitGetAsEntry?.();
      if (!entry) continue;
      if (entry.isDirectory) {
        rejected.push({ name: entry.name, reason: 'tenders.responses.documents.ignored-reason-unsupported-file-type' });
        continue;
      }
      if (entry.isFile) {
        const file = item.getAsFile();
        if (!file) continue;
        if (!this.isAccepted(file.name)) {
          rejected.push({ name: file.name, reason: 'tenders.responses.documents.ignored-reason-unsupported-file-type' });
          continue;
        }
        files.push(file);
      }
    }

    if (rejected.length) this.filesRejected.emit(rejected);
    if (files.length) this.filesSelected.emit(files);
  }

  private emitFiles(files: File[]): void {
    const accepted = files.filter(f => this.isAccepted(f.name));
    const rejected = files
      .filter(f => !this.isAccepted(f.name))
      .map(f => ({
        name: f.name,
        reason: 'tenders.responses.documents.ignored-reason-unsupported-file-type',
      }));
    if (rejected.length) this.filesRejected.emit(rejected);
    if (accepted.length) this.filesSelected.emit(accepted);
  }

  private isAccepted(name: string): boolean {
    return this.accept.split(',').some(ext => name.toLowerCase().endsWith(ext.trim()));
  }
}
